import type { ReactNode, Ref } from "react";
import { useCallback, useImperativeHandle, useRef, useState } from "react";
import { View } from "react-native";

import Button from "$src/core/components/atoms/Button/Button";
import Modal from "$src/core/components/atoms/Modal";
import type { ModalRef } from "$src/core/components/atoms/Modal/types";
import Text from "$src/core/components/atoms/Text/Text";
import useTranslation from "$src/core/hooks/use-translation";
import CustomPlatform from "$src/core/utils/platform/platform";

import $ from "./ConfirmationModal.styles";

// Android TV opens a modal window with nothing focused, so the first remote press was
// spent claiming focus instead of choosing. The activation dialog only looked right
// because its coupon TextInput takes focus by itself. tvOS, phone and web all focus
// something already, so they keep their behaviour.
const NEEDS_ENTRY_FOCUS = CustomPlatform.select({
  androidTV: true,
  default: false,
});

type Props = {
  ref?: Ref<ModalRef>;
  title: string;
  description?: string;
  cancelLabel?: string;
  confirmLabel?: string;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
  children?: ReactNode;
};

const ConfirmationModal = ({
  ref: forwardedRef,
  title,
  description,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  children,
}: Props) => {
  const { t } = useTranslation();

  const ref = useRef<ModalRef | null>(null);
  const cancelRef = useRef<View | null>(null);
  const hasClaimedEntryFocus = useRef(false);

  // Claim once per presentation, and on cancel rather than confirm: a destructive
  // dialog must not open on its destructive button, and a later re-layout must not
  // pull focus back off the user.
  const claimEntryFocus = useCallback(() => {
    if (hasClaimedEntryFocus.current) return;

    hasClaimedEntryFocus.current = true;
    cancelRef.current?.requestTVFocus();
  }, []);

  useImperativeHandle(
    forwardedRef,
    () => ({
      present: () => {
        hasClaimedEntryFocus.current = false;
        ref.current?.present();
      },
      dismiss: () => ref.current?.dismiss(),
    }),
    [ref],
  );

  const [isConfirming, setIsConfirming] = useState(false);

  const handleCancel = () => {
    onCancel();
    ref.current?.dismiss();
  };

  const handleConfirm = async () => {
    try {
      setIsConfirming(true);
      await onConfirm();
      ref.current?.dismiss();
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Modal ref={ref} title={title}>
      {description && (
        <Text preset="body" accessibilityRole="text" style={$.description}>
          {description}
        </Text>
      )}
      {children}
      <View
        style={$.actions}
        onLayout={NEEDS_ENTRY_FOCUS ? claimEntryFocus : undefined}
      >
        <Button
          ref={cancelRef}
          name="confirmation_modal_cancel"
          style={$.action}
          preset="transparent"
          label={cancelLabel ?? t("COMMON/CANCEL")}
          onPress={handleCancel}
          isDisabled={isConfirming}
        />
        <Button
          name="confirmation_modal_confirm"
          style={$.action}
          preset="primary"
          label={confirmLabel ?? t("COMMON/CONFIRM")}
          onPress={handleConfirm}
          isLoading={isConfirming}
        />
      </View>
    </Modal>
  );
};

export default ConfirmationModal;
